import React, { useState } from 'react';
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ValuationTools } from "@/components/ValuationTools";
import { ValuationCalculator } from "@/components/valuation/ValuationCalculator";
import { ProfessionalReport } from "@/components/valuation/ProfessionalReport";
import { Button } from "@/components/ui/button";
import { Zap, FileSpreadsheet, Map as MapIcon, Database, TrendingUp, Search, ArrowLeft } from "lucide-react";

export default function ValuationPage() {
  const [currentValuation, setCurrentValuation] = useState<any>(null);
  const [showReport, setShowReport] = useState(false);

  const handleValuationSuccess = (valuation: any) => {
    setCurrentValuation(valuation);
    setShowReport(true);
    // Scroll suave hacia el reporte
    setTimeout(() => {
      const reportElement = document.getElementById('valuation-report');
      if (reportElement) {
        reportElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-parchment">
      <Navbar />
      <main className="pt-20">
        {/* Hero de Valorización */}
        <section className="py-24 bg-ink text-parchment border-b-2 border-ink">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="max-w-2xl xl:max-w-3xl">
                <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-6 block">Inteligencia de Datos</span>
                <h1 className="text-3xl md:text-4xl lg:text-[52px] font-display font-black uppercase italic leading-[0.9] mb-8 text-white">
                  Valorización <br /> <span className="text-terracotta">con Precisión IA</span>
                </h1>
                <p className="text-lg font-serif italic opacity-60 leading-relaxed max-w-2xl mb-12">
                  Obtén en segundos el valor comercial de venta y arriendo de cualquier propiedad gracias a nuestro avanzado algoritmo de machine learning.
                </p>
                <div className="flex gap-4">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="h-16 px-12 text-lg border-parchment text-parchment hover:bg-parchment hover:text-ink"
                    onClick={() => {
                      const calculatorElement = document.getElementById('valuation-calculator');
                      if (calculatorElement) calculatorElement.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    INICIAR CÁLCULO
                  </Button>
                </div>
              </div>

              {/* Calculadora embebida en el Hero para fácil acceso */}
              <div id="valuation-calculator" className="relative z-10">
                <div className="absolute -inset-4 bg-terracotta/20 rotate-2 -z-10" />
                <ValuationCalculator onSuccess={handleValuationSuccess} />
              </div>
            </div>
          </div>
        </section>

        {/* Sección de Reporte (Condicional) */}
        {showReport && currentValuation && (
          <section id="valuation-report" className="py-24 bg-parchment border-b-4 border-ink animate-in slide-in-from-bottom-8 duration-1000">
            <div className="container mx-auto px-4">
              <div className="flex justify-between items-end mb-12">
                <h2 className="text-4xl font-display font-black uppercase italic leading-none">Resultado del Análisis</h2>
                <Button 
                  variant="outline" 
                  onClick={() => setShowReport(false)}
                  className="border-ink text-ink"
                >
                  CERRAR REPORTE
                </Button>
              </div>
              <ProfessionalReport valuation={currentValuation} />
            </div>
          </section>
        )}

        {/* Detalle del Informe */}
        <section className="py-24 bg-parchment">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-16">
              <h2 className="text-5xl font-display font-black uppercase italic mb-8">Informes que Generan <br /> Confianza</h2>
              <p className="text-xl font-serif italic text-ink/60">
                Nuestros reportes de tasación son los más completos del mercado, diseñados para ser presentados directamente a tus clientes con tu propia marca.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { title: "Valor de Mercado", desc: "Sugerencia de valor máximo y mínimo esperado según el estado de la propiedad.", icon: TrendingUp },
                { title: "Evolutivo UF/M2", desc: "Historial de precios de los últimos 2 años para la propiedad y el sector.", icon: Search },
                { title: "Tiempo de Venta", desc: "Estimación del tiempo esperado de colocación para venta y arriendo.", icon: Database },
                { title: "Competitividad", desc: "Análisis del nivel de competencia en el sector y evolución reciente.", icon: Zap },
                { title: "Comparables", desc: "Listado de propiedades publicadas más similares de venta y arriendo.", icon: FileSpreadsheet },
                { title: "Datos CBR", desc: "Información del Conservador de Bienes Raíces con propiedades vendidas.", icon: MapIcon }
              ].map((item, i) => (
                <div key={i} className="brutalist-border p-8 hover:bg-ink hover:text-parchment transition-all group">
                  <item.icon className="h-10 w-10 text-terracotta mb-6" />
                  <h3 className="text-2xl font-display font-black uppercase italic mb-4">{item.title}</h3>
                  <p className="font-serif italic opacity-70">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ValuationTools />
      </main>
      <Footer />
    </div>
  );
}
